
import React, { useEffect, useState } from 'react';
import { Brand, SiteConfig, Aggregator, AggregatorImageProfile, AggregatorImageVariant } from '../../../types';
import { Plus, Trash2, Upload, Store, Image as ImageIcon, X, Save, RefreshCw, Edit2, Monitor, Bell, Search, Calendar, Activity, AlertTriangle, Download, CheckCircle, Layers, Settings, LayoutGrid } from 'lucide-react';
import { apiClient } from '../../../services/apiClient';
import { saveProfile as saveAggregatorProfile } from '../../../services/aggregatorImageProfile';

interface SettingsViewProps {
    brands: Brand[];
    onUpdateBrands: (brands: Brand[]) => void;
    aggregators: Aggregator[];
    onUpdateAggregators: (aggs: Aggregator[]) => void;
    siteConfig: SiteConfig;
    onUpdateSiteConfig: (config: SiteConfig) => void;
    showToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const SettingsView: React.FC<SettingsViewProps> = ({ brands, onUpdateBrands, aggregators, onUpdateAggregators, siteConfig, onUpdateSiteConfig, showToast }) => {
    const [activeTab, setActiveTab] = useState<'general' | 'brands' | 'platforms' | 'trace'>('general');
    const [activity, setActivity] = useState<any[]>([]);
    const [activitySearch, setActivitySearch] = useState('');
    const [activityType, setActivityType] = useState<'all' | 'info' | 'success' | 'warning' | 'error' | 'approve' | 'reject' | 'comment' | 'return'>('all');
    const [isActivityLoading, setIsActivityLoading] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    
    // Brand State
    const [editingId, setEditingId] = useState<string | null>(null);
    const [brandName, setBrandName] = useState('');
    const [brandLogo, setBrandLogo] = useState<File | null>(null);
    const [existingLogoUrl, setExistingLogoUrl] = useState<string | undefined>(undefined);

    // Aggregator State
    const [aggEditingId, setAggEditingId] = useState<string | null>(null);
    const [aggName, setAggName] = useState('');
    const [aggActive, setAggActive] = useState(true);
    const [aggImageEnabled, setAggImageEnabled] = useState(false);
    const [aggVariants, setAggVariants] = useState<AggregatorImageVariant[]>([]);

    // Site Config State
    const [appName, setAppName] = useState(siteConfig.appName);
    const [appSubTitle, setAppSubTitle] = useState(siteConfig.appSubTitle);
    const [appLogo, setAppLogo] = useState<File | null>(null);
    const [loginMessage, setLoginMessage] = useState(siteConfig.loginMessage || '');
    const [currencyCode, setCurrencyCode] = useState(siteConfig.currencyCode || 'QAR');

    const fileToDataUrl = (file: File) =>
        new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });

    // --- BRAND HANDLERS ---
    const handleSaveBrand = async () => {
        if (!brandName.trim()) {
            showToast('error', 'Validation Error', 'Brand name is required.');
            return;
        }

        const logoUrl = brandLogo ? await fileToDataUrl(brandLogo) : existingLogoUrl;

        try {
            const savedList = await apiClient.brands.upsert({
                id: editingId ?? Date.now().toString(),
                name: brandName,
                logo: logoUrl,
            });
            onUpdateBrands(savedList as Brand[]);
            showToast('success', editingId ? 'Brand Updated' : 'Brand Added', `${brandName} saved.`);
            setEditingId(null);
        } catch (error) {
            showToast('error', 'Save Failed', 'Could not persist brand. Please retry.');
        }

        // Reset form
        setBrandName('');
        setBrandLogo(null);
        setExistingLogoUrl(undefined);
    };

    const handleEditBrand = (brand: Brand) => {
        setEditingId(brand.id);
        setBrandName(brand.name);
        setExistingLogoUrl(brand.logo);
        setBrandLogo(null); // Reset file input for new uploads
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setBrandName('');
        setBrandLogo(null);
        setExistingLogoUrl(undefined);
    };

    const handleDeleteBrand = (id: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent triggering other clicks
        if (confirm('Are you sure you want to delete this brand? This will affect submission filters.')) {
            const updated = brands.filter(b => b.id !== id);
            onUpdateBrands(updated);
            showToast('info', 'Brand Removed', 'The brand has been removed locally.');
            if (editingId === id) handleCancelEdit();
        }
    };

    const handleBrandLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setBrandLogo(e.target.files[0]);
        }
    };

    // --- SITE CONFIG HANDLERS ---
    const handleSaveSiteConfig = async () => {
        const newLogo = appLogo ? await fileToDataUrl(appLogo) : siteConfig.logo;
        
        try {
            await apiClient.siteConfig.update({
                appName,
                appSubTitle,
                logo: newLogo,
                loginMessage,
                currencyCode
            });
            onUpdateSiteConfig({
                appName,
                appSubTitle,
                logo: newLogo,
                loginMessage,
                currencyCode
            });
            showToast('success', 'Settings Saved', 'Site configuration updated successfully.');
        } catch (error) {
            showToast('error', 'Save Failed', 'Could not update settings. Please try again.');
        }
    };

    const handleAppLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setAppLogo(e.target.files[0]);
        }
    };

    // --- AGGREGATOR HANDLERS ---
    const defaultVariant = (): AggregatorImageVariant => ({
        key: '',
        label: '',
        width: 1000,
        height: 1000,
        ratioLabel: '',
        format: 'jpg',
        quality: undefined,
    });

    const resetAggregatorForm = () => {
        setAggEditingId(null);
        setAggName('');
        setAggActive(true);
        setAggImageEnabled(false);
        setAggVariants([]);
    };

    const sanitizeVariants = (variants: AggregatorImageVariant[]): AggregatorImageVariant[] =>
        (variants || []).map((v) => ({
            key: (v.key || '').trim(),
            label: (v.label || '').trim(),
            width: Number.isFinite(v.width) ? Number(v.width) : 0,
            height: Number.isFinite(v.height) ? Number(v.height) : 0,
            ratioLabel: (v.ratioLabel || '').trim() || undefined,
            format: v.format,
            quality: v.quality === undefined || v.quality === null || Number.isNaN(v.quality) ? undefined : Number(v.quality),
        }));

    const validateImageProfile = (): AggregatorImageProfile | null => {
        if (!aggImageEnabled) return { enabled: false, variants: [] };
        const sanitized = sanitizeVariants(aggVariants);
        if (!sanitized.length) {
            showToast('error', 'Validation Error', 'Add at least one image variant or disable image variants.');
            return null;
        }
        const seenKeys = new Set<string>();
        for (const variant of sanitized) {
            if (!variant.key) {
                showToast('error', 'Validation Error', 'Variant key is required for all image variants.');
                return null;
            }
            if (seenKeys.has(variant.key)) {
                showToast('error', 'Validation Error', 'Variant keys must be unique.');
                return null;
            }
            seenKeys.add(variant.key);
            if (!variant.label) {
                showToast('error', 'Validation Error', 'Variant label is required for all image variants.');
                return null;
            }
            if (!Number.isInteger(variant.width) || variant.width <= 0 || !Number.isInteger(variant.height) || variant.height <= 0) {
                showToast('error', 'Validation Error', 'Width and height must be positive integers.');
                return null;
            }
            if (variant.quality !== undefined) {
                if (!Number.isInteger(variant.quality) || variant.quality < 1 || variant.quality > 100) {
                    showToast('error', 'Validation Error', 'Quality must be between 1 and 100.');
                    return null;
                }
            }
        }
        return { enabled: true, variants: sanitized };
    };

    const handleAddVariant = () => {
        setAggVariants((prev) => [...prev, defaultVariant()]);
    };

    const handleVariantChange = (index: number, field: keyof AggregatorImageVariant, value: string) => {
        setAggVariants((prev) =>
            prev.map((variant, i) => {
                if (i !== index) return variant;
                if (field === 'width' || field === 'height') {
                    const parsed = parseInt(value || '0', 10);
                    return { ...variant, [field]: Number.isFinite(parsed) ? parsed : 0 };
                }
                if (field === 'quality') {
                    const parsed = parseInt(value || '0', 10);
                    return { ...variant, quality: value === '' ? undefined : (Number.isFinite(parsed) ? parsed : undefined) };
                }
                if (field === 'format') {
                    return { ...variant, format: value ? (value as AggregatorImageVariant['format']) : undefined };
                }
                return { ...variant, [field]: value };
            })
        );
    };

    const handleRemoveVariant = (index: number) => {
        setAggVariants((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSaveAggregator = async () => {
        const name = aggName.trim();
        if (!name) {
            showToast('error', 'Validation Error', 'Platform name is required.');
            return;
        }
        const imageProfile = validateImageProfile();
        if (!imageProfile) return;
        try {
            const updated = aggEditingId
                ? await apiClient.aggregators.update(aggEditingId, { name, active: aggActive, imageProfile })
                : await apiClient.aggregators.create({ name, active: aggActive, imageProfile });
            if (Array.isArray(updated)) onUpdateAggregators(updated as Aggregator[]);
            resetAggregatorForm();
            showToast('success', aggEditingId ? 'Platform Updated' : 'Platform Added', `${name} saved.`);
        } catch (error) {
            showToast('error', 'Save Failed', 'Could not save platform.');
        }
    };

    const handleEditAggregator = (agg: Aggregator) => {
        setAggEditingId(agg.id);
        setAggName(agg.name);
        setAggActive(Boolean(agg.active));
        const profile = agg.imageProfile;
        setAggImageEnabled(Boolean(profile?.enabled));
        setAggVariants(
            profile?.variants?.length
                ? profile.variants.map((v) => ({
                    key: v.key || '',
                    label: v.label || '',
                    width: Number.isFinite(v.width) ? Number(v.width) : 1000,
                    height: Number.isFinite(v.height) ? Number(v.height) : 1000,
                    ratioLabel: v.ratioLabel || '',
                    format: v.format,
                    quality: v.quality,
                }))
                : []
        );
    };

    const handleDeleteAggregator = async (id: string) => {
        try {
            const updated = await apiClient.aggregators.delete(id);
            saveAggregatorProfile(id, null);
            if (Array.isArray(updated)) onUpdateAggregators(updated as Aggregator[]);
            showToast('warning', 'Platform Deactivated', 'The platform has been disabled.');
        } catch (error) {
            showToast('error', 'Delete Failed', 'Unable to deactivate platform.');
        }
    };

    // --- TRACE / ACTIVITY LOG ---
    useEffect(() => {
        if (activeTab !== 'trace') return;
        let cancelled = false;
        const load = async () => {
            setIsActivityLoading(true);
            try {
                const res = await apiClient.activity.list();
                if (!cancelled && Array.isArray(res)) {
                    setActivity(
                        res
                            .map((n: any) => ({
                                ...n,
                                time: n.time || n.timestamp || n.createdAt,
                            }))
                            .sort((a, b) => new Date(b.time || '').getTime() - new Date(a.time || '').getTime())
                    );
                }
            } catch (err) {
                if (!cancelled) showToast('error', 'Trace Log', 'Unable to load activity history.');
            } finally {
                if (!cancelled) setIsActivityLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [activeTab, showToast]);

    const handleExport = (
        list: any[],
        search: string,
        typeFilter: typeof activityType,
        format: 'csv' | 'json'
    ) => {
        const filtered = list.filter((n) => {
            const term = search.toLowerCase();
            const matchesSearch =
                !term ||
                (n.title || '').toLowerCase().includes(term) ||
                (n.message || '').toLowerCase().includes(term) ||
                (n.user || '').toLowerCase().includes(term) ||
                (n.targetView || '').toLowerCase().includes(term) ||
                (n.targetId || '').toLowerCase().includes(term);
            const matchesType = typeFilter === 'all' ? true : (n.type || '').toLowerCase() === typeFilter;
            return matchesSearch && matchesType;
        });

        if (format === 'json') {
            const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `trace_${new Date().toISOString()}.json`;
            a.click();
            URL.revokeObjectURL(url);
            return;
        }

        // CSV
        const header = ['id', 'source', 'type', 'title', 'message', 'time', 'user', 'stage', 'action', 'targetView', 'targetId'];
        const rows = filtered.map((n) =>
            header
                .map((key) => {
                    const val: any = (n as any)[key];
                    const safe = val === undefined || val === null ? '' : String(val).replace(/"/g, '""');
                    return `"${safe}"`;
                })
                .join(',')
        );
        const csv = [header.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `trace_${new Date().toISOString()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const NavButton = ({ id, label, icon, active, onClick }: any) => (
        <button
            onClick={onClick}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                ${active 
                    ? 'bg-zinc-900 text-white shadow-md shadow-zinc-200' 
                    : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                }
            `}
        >
            {icon}
            {label}
        </button>
    );

    return (
        <div className="flex h-[calc(100vh-100px)] w-full gap-0 bg-zinc-50/50">
            {/* Sidebar */}
            <div className="w-64 bg-white border-r border-zinc-200 flex flex-col shrink-0">
                <div className="p-6 border-b border-zinc-100">
                    <h2 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Settings
                    </h2>
                </div>
                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    <NavButton 
                        id="general" 
                        label="General" 
                        icon={<Monitor className="w-4 h-4" />} 
                        active={activeTab === 'general'} 
                        onClick={() => setActiveTab('general')} 
                    />
                    <NavButton 
                        id="brands" 
                        label="Brands" 
                        icon={<Store className="w-4 h-4" />} 
                        active={activeTab === 'brands'} 
                        onClick={() => setActiveTab('brands')} 
                    />
                    <NavButton 
                        id="platforms" 
                        label="Platforms" 
                        icon={<Layers className="w-4 h-4" />} 
                        active={activeTab === 'platforms'} 
                        onClick={() => setActiveTab('platforms')} 
                    />
                    <div className="pt-4 pb-2">
                        <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider px-3 mb-2">System</div>
                        <NavButton 
                            id="trace" 
                            label="Activity Log" 
                            icon={<Activity className="w-4 h-4" />} 
                            active={activeTab === 'trace'} 
                            onClick={() => setActiveTab('trace')} 
                        />
                    </div>
                </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-zinc-50/50">
                <header className="bg-white border-b border-zinc-200 px-8 py-5 flex justify-between items-center shrink-0">
                    <div>
                        <h1 className="text-xl font-bold text-zinc-900">
                            {activeTab === 'general' && 'General Configuration'}
                            {activeTab === 'brands' && 'Brand Management'}
                            {activeTab === 'platforms' && 'Platform Integrations'}
                            {activeTab === 'trace' && 'Activity Log'}
                        </h1>
                        <p className="text-sm text-zinc-500 mt-1">
                            {activeTab === 'general' && 'Manage application branding and global settings.'}
                            {activeTab === 'brands' && 'Configure the brands available for campaigns.'}
                            {activeTab === 'platforms' && 'Manage external aggregator platform connections.'}
                            {activeTab === 'trace' && 'Audit trail of system events and notifications.'}
                        </p>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                    <div className="w-full">
                        {activeTab === 'general' && (
                            <div className="w-full">
                                <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-6 space-y-6">
                                    <h3 className="font-bold text-zinc-900 flex items-center gap-2">
                                        <Monitor className="w-5 h-5 text-teal-600" />
                                        Site Branding
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-zinc-500 uppercase">App Name</label>
                                            <input 
                                                type="text" 
                                                value={appName}
                                                onChange={(e) => setAppName(e.target.value)}
                                                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                            />
                                        </div>
                                         <div className="space-y-2">
                                            <label className="text-xs font-bold text-zinc-500 uppercase">Subtitle</label>
                                            <input 
                                                type="text" 
                                                value={appSubTitle}
                                                onChange={(e) => setAppSubTitle(e.target.value)}
                                                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-zinc-500 uppercase">Login Screen Message</label>
                                            <textarea
                                                value={loginMessage}
                                                onChange={(e) => setLoginMessage(e.target.value)}
                                                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100 min-h-[100px] resize-none"
                                                placeholder="Message displayed on login screen..."
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-zinc-500 uppercase">Currency</label>
                                            <select
                                                value={currencyCode}
                                                onChange={(e) => setCurrencyCode(e.target.value)}
                                                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                            >
                                                {['QAR','USD','AED','SAR','KWD','OMR','BHD','EUR','GBP'].map((code) => (
                                                    <option key={code} value={code}>{code}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-zinc-500 uppercase">Header Logo</label>
                                            <label className="block cursor-pointer group">
                                                <input type="file" accept="image/*" className="hidden" onChange={handleAppLogoChange} />
                                                <div className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-zinc-300 hover:border-teal-400 bg-zinc-50 hover:bg-white transition-colors">
                                                    <div className="w-10 h-10 rounded-lg bg-zinc-200 flex items-center justify-center overflow-hidden shrink-0">
                                                        {appLogo ? (
                                                            <img src={URL.createObjectURL(appLogo)} className="w-full h-full object-cover" alt="preview" />
                                                        ) : siteConfig.logo ? (
                                                            <img src={siteConfig.logo} className="w-full h-full object-cover" alt="current" />
                                                        ) : (
                                                            <Store className="w-5 h-5 text-zinc-400" />
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="text-xs font-bold text-zinc-600 group-hover:text-teal-600">Upload New Logo</div>
                                                        <div className="text-[10px] text-zinc-400">Recommended 100x100</div>
                                                    </div>
                                                    <Upload className="w-4 h-4 text-zinc-400 group-hover:text-teal-500" />
                                                </div>
                                            </label>
                                        </div>
                                        <button 
                                            onClick={handleSaveSiteConfig}
                                            className="w-full py-2.5 bg-zinc-900 text-white font-bold rounded-xl hover:bg-zinc-800 shadow-lg shadow-zinc-200 active:scale-95 transition-all text-sm"
                                        >
                                            Save Branding
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'brands' && (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-2 space-y-4">
                                    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
                                        <div className="px-6 py-4 border-b border-zinc-100 bg-zinc-50 flex justify-between items-center">
                                            <h3 className="font-bold text-zinc-700 text-sm">Active Brands</h3>
                                            <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">{brands.length} Total</span>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-6">
                                            {brands.map((brand) => (
                                                <div 
                                                    key={brand.id} 
                                                    className={`flex items-center justify-between p-4 rounded-xl border transition-all group relative
                                                        ${editingId === brand.id ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-100' : 'border-zinc-100 bg-white hover:shadow-md hover:border-zinc-200'}
                                                    `}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-12 h-12 rounded-xl bg-zinc-50 border border-zinc-100 flex items-center justify-center overflow-hidden shrink-0">
                                                            {brand.logo ? (
                                                                <img src={brand.logo} alt={brand.name} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <Store className="w-5 h-5 text-zinc-300" />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h4 className="font-bold text-zinc-800 text-sm">{brand.name}</h4>
                                                            <span className="text-[10px] font-mono text-zinc-400">ID: {brand.id}</span>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity relative z-10">
                                                        <button 
                                                            onClick={() => handleEditBrand(brand)}
                                                            className="p-2 text-zinc-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                                            title="Edit Brand"
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                        </button>
                                                        <button 
                                                            onClick={(e) => handleDeleteBrand(brand.id, e)}
                                                            className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Remove Brand"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            {brands.length === 0 && (
                                                <div className="col-span-full p-12 text-center text-zinc-400">
                                                    No brands found. Add one to get started.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="lg:col-span-1">
                                    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-6 sticky top-6">
                                        <h3 className="font-bold text-zinc-900 mb-4 flex items-center gap-2">
                                            {editingId ? <Edit2 className="w-5 h-5 text-amber-500" /> : <Plus className="w-5 h-5 text-emerald-600" />}
                                            {editingId ? 'Edit Brand' : 'Add New Brand'}
                                        </h3>
                                        
                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-zinc-500 uppercase">Brand Name</label>
                                                <input 
                                                    type="text" 
                                                    value={brandName}
                                                    onChange={(e) => setBrandName(e.target.value)}
                                                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                                    placeholder="e.g. Burger King"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-zinc-500 uppercase">Logo (Optional)</label>
                                                <label className="block cursor-pointer">
                                                    <input type="file" accept="image/*" className="hidden" onChange={handleBrandLogoChange} />
                                                    <div className={`
                                                        w-full h-32 rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-center p-4 transition-colors relative
                                                        ${(brandLogo || existingLogoUrl) ? 'bg-teal-50 border-teal-200' : 'bg-zinc-50 border-zinc-200 hover:bg-zinc-100 hover:border-teal-200'}
                                                    `}>
                                                        {(brandLogo || existingLogoUrl) ? (
                                                            <div className="relative w-full h-full">
                                                                <img 
                                                                    src={brandLogo ? URL.createObjectURL(brandLogo) : existingLogoUrl} 
                                                                    alt="Preview" 
                                                                    className="w-full h-full object-contain"
                                                                />
                                                                <button 
                                                                    onClick={(e) => { e.preventDefault(); setBrandLogo(null); setExistingLogoUrl(undefined); }}
                                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <Upload className="w-6 h-6 text-zinc-400 mb-2" />
                                                                <span className="text-xs font-bold text-zinc-500">Click to upload logo</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </label>
                                            </div>

                                            <div className="flex gap-2">
                                                {editingId && (
                                                    <button 
                                                        onClick={handleCancelEdit}
                                                        className="flex-1 py-3 bg-zinc-100 text-zinc-600 font-bold rounded-xl hover:bg-zinc-200 transition-all"
                                                    >
                                                        Cancel
                                                    </button>
                                                )}
                                                <button 
                                                    onClick={handleSaveBrand}
                                                    className={`flex-1 py-3 font-bold rounded-xl shadow-lg transition-all active:scale-95 text-white
                                                        ${editingId 
                                                            ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-200' 
                                                            : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200'
                                                        }
                                                    `}
                                                >
                                                    {editingId ? 'Update' : 'Add Brand'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'platforms' && (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-2">
                                    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
                                        <div className="px-6 py-4 border-b border-zinc-100 bg-zinc-50 flex justify-between items-center">
                                            <h3 className="font-bold text-zinc-700 text-sm">Aggregator Platforms</h3>
                                            <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">{aggregators.length} Total</span>
                                        </div>
                                        <div className="divide-y border-t border-zinc-100">
                                            {aggregators.map((agg) => (
                                                <div key={agg.id} className="flex items-center justify-between px-6 py-4 hover:bg-zinc-50 transition-colors">
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-zinc-900 text-sm">{agg.name}</span>
                                                        <span className="text-[10px] font-mono text-zinc-400">ID: {agg.id}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-xs font-bold px-2 py-1 rounded-full border ${agg.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-zinc-100 text-zinc-500 border-zinc-200'}`}>
                                                            {agg.active ? 'Active' : 'Inactive'}
                                                        </span>
                                                        <button
                                                            onClick={() => handleEditAggregator(agg)}
                                                            className="p-2 text-zinc-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                                            title="Edit Platform"
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteAggregator(agg.id)}
                                                            className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Deactivate Platform"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            {aggregators.length === 0 && (
                                                <div className="p-6 text-sm text-zinc-500 text-center">No platforms yet. Add one on the left.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="lg:col-span-1">
                                    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-6 sticky top-6">
                                        <h3 className="font-bold text-zinc-900 mb-4 flex items-center gap-2">
                                            {aggEditingId ? <Edit2 className="w-5 h-5 text-amber-500" /> : <Plus className="w-5 h-5 text-emerald-600" />}
                                            {aggEditingId ? 'Edit Platform' : 'Add Platform'}
                                        </h3>
                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-zinc-500 uppercase">Platform Name</label>
                                                <input
                                                    type="text"
                                                    value={aggName}
                                                    onChange={(e) => setAggName(e.target.value)}
                                                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                                    placeholder="e.g. Talabat"
                                                />
                                            </div>
                                            <label className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
                                                <input type="checkbox" checked={aggActive} onChange={(e) => setAggActive(e.target.checked)} />
                                                Active
                                            </label>

                                            <div className="border border-dashed border-zinc-200 rounded-xl p-4 bg-zinc-50/70 space-y-3">
                                                <div className="flex items-center justify-between gap-3">
                                                    <div className="flex items-center gap-2">
                                                        <ImageIcon className="w-4 h-4 text-zinc-500" />
                                                        <div>
                                                            <p className="text-sm font-semibold text-zinc-800">Image Settings</p>
                                                            <p className="text-xs text-zinc-500">Define required image variants for this platform.</p>
                                                        </div>
                                                    </div>
                                                    <label className="flex items-center gap-2 text-xs font-bold text-zinc-700 uppercase">
                                                        <input
                                                            type="checkbox"
                                                            checked={aggImageEnabled}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                setAggImageEnabled(checked);
                                                                if (checked && aggVariants.length === 0) setAggVariants([defaultVariant()]);
                                                            }}
                                                        />
                                                        Enable
                                                    </label>
                                                </div>

                                                {aggImageEnabled && (
                                                    <div className="space-y-3">
                                                        {aggVariants.map((variant, idx) => (
                                                            <div key={idx} className="rounded-xl border border-zinc-200 bg-white p-3 space-y-3">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-xs font-bold text-zinc-500 uppercase">Variant {idx + 1}</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveVariant(idx)}
                                                                        className="text-xs text-red-500 hover:text-red-600"
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                                <div className="grid grid-cols-2 gap-3">
                                                                    <div className="space-y-1">
                                                                        <label className="text-[11px] font-bold text-zinc-500 uppercase">Label</label>
                                                                        <input
                                                                            type="text"
                                                                            value={variant.label}
                                                                            onChange={(e) => handleVariantChange(idx, 'label', e.target.value)}
                                                                            className="w-full bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-sm font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                                                            placeholder="Main Image"
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-1">
                                                                        <label className="text-[11px] font-bold text-zinc-500 uppercase">Key</label>
                                                                        <input
                                                                            type="text"
                                                                            value={variant.key}
                                                                            onChange={(e) => handleVariantChange(idx, 'key', e.target.value)}
                                                                            className="w-full bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-sm font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                                                            placeholder="talabat_main"
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-1">
                                                                        <label className="text-[11px] font-bold text-zinc-500 uppercase">Width (px)</label>
                                                                        <input
                                                                            type="number"
                                                                            min={1}
                                                                            value={variant.width}
                                                                            onChange={(e) => handleVariantChange(idx, 'width', e.target.value)}
                                                                            className="w-full bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-sm font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-1">
                                                                        <label className="text-[11px] font-bold text-zinc-500 uppercase">Height (px)</label>
                                                                        <input
                                                                            type="number"
                                                                            min={1}
                                                                            value={variant.height}
                                                                            onChange={(e) => handleVariantChange(idx, 'height', e.target.value)}
                                                                            className="w-full bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-sm font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-1">
                                                                        <label className="text-[11px] font-bold text-zinc-500 uppercase">Ratio Label (optional)</label>
                                                                        <input
                                                                            type="text"
                                                                            value={variant.ratioLabel || ''}
                                                                            onChange={(e) => handleVariantChange(idx, 'ratioLabel', e.target.value)}
                                                                            className="w-full bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-sm font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                                                            placeholder="3:2"
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-1">
                                                                        <label className="text-[11px] font-bold text-zinc-500 uppercase">Format</label>
                                                                        <select
                                                                            value={variant.format || ''}
                                                                            onChange={(e) => handleVariantChange(idx, 'format', e.target.value)}
                                                                            className="w-full bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-sm font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                                                        >
                                                                            <option value="">Any</option>
                                                                            <option value="jpg">JPG</option>
                                                                            <option value="png">PNG</option>
                                                                            <option value="webp">WEBP</option>
                                                                        </select>
                                                                    </div>
                                                                    <div className="space-y-1">
                                                                        <label className="text-[11px] font-bold text-zinc-500 uppercase">Quality (1-100)</label>
                                                                        <input
                                                                            type="number"
                                                                            min={1}
                                                                            max={100}
                                                                            value={variant.quality ?? ''}
                                                                            onChange={(e) => handleVariantChange(idx, 'quality', e.target.value)}
                                                                            className="w-full bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-sm font-semibold text-zinc-800 outline-none focus:ring-2 focus:ring-teal-100"
                                                                            placeholder="90"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                        <button
                                                            type="button"
                                                            onClick={handleAddVariant}
                                                            className="w-full py-2 text-sm font-semibold text-teal-700 border border-teal-100 rounded-lg bg-teal-50 hover:bg-teal-100 transition-colors"
                                                        >
                                                            Add Variant
                                                        </button>
                                                        <p className="text-[11px] text-zinc-500">Keys must be unique per platform. Width/height are required; format/quality are optional.</p>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                {aggEditingId && (
                                                    <button
                                                        onClick={resetAggregatorForm}
                                                        className="flex-1 py-3 bg-zinc-100 text-zinc-600 font-bold rounded-xl hover:bg-zinc-200 transition-all"
                                                    >
                                                        Cancel
                                                    </button>
                                                )}
                                                <button
                                                    onClick={handleSaveAggregator}
                                                    className="flex-1 py-3 font-bold rounded-xl shadow-lg transition-all active:scale-95 text-white bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200"
                                                >
                                                    {aggEditingId ? 'Update Platform' : 'Add Platform'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'trace' && (
                            <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-6 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-bold text-zinc-900 text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-teal-600" /> Trace / Activity Log</h3>
                                        <p className="text-sm text-zinc-500">Combined audit of notifications and workflow actions. Entries persist until deleted in the database.</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                setActiveTab('trace');
                                                setIsActivityLoading(true);
                                                apiClient.activity.list().then((res: any) => {
                                                    setActivity(res);
                                                    setIsActivityLoading(false);
                                                }).catch(() => setIsActivityLoading(false));
                                            }}
                                            className="px-3 py-2 text-xs font-bold text-teal-600 bg-teal-50 border border-teal-100 rounded-lg hover:bg-teal-100 transition-colors flex items-center gap-2"
                                        >
                                            <RefreshCw className="w-4 h-4" /> Refresh
                                        </button>
                                        <button
                                            onClick={async () => {
                                                if (!confirm('This will delete all activity log entries (notifications + workflow logs). Continue?')) return;
                                                setIsClearing(true);
                                                try {
                                                    await apiClient.activity.clear();
                                                    setActivity([]);
                                                    showToast('success', 'Trace Cleared', 'Activity log was cleared. New events will appear as they happen.');
                                                } catch {
                                                    showToast('error', 'Clear Failed', 'Unable to clear activity log.');
                                                } finally {
                                                    setIsClearing(false);
                                                }
                                            }}
                                            className="px-3 py-2 text-xs font-bold text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
                                            disabled={isClearing}
                                        >
                                            <Trash2 className="w-4 h-4" /> {isClearing ? 'Clearing...' : 'Clear Log'}
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 flex-wrap">
                                    <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2">
                                        <Search className="w-4 h-4 text-zinc-400" />
                                        <input
                                            type="text"
                                            placeholder="Search title, message, user, or ID"
                                            value={activitySearch}
                                            onChange={(e) => setActivitySearch(e.target.value)}
                                            className="bg-transparent outline-none text-sm font-semibold text-zinc-700"
                                        />
                                    </div>
                                    <select
                                        value={activityType}
                                        onChange={(e) => setActivityType(e.target.value as any)}
                                        className="bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-sm font-semibold text-zinc-700"
                                    >
                                        <option value="all">All Types</option>
                                        <option value="info">Info</option>
                                        <option value="success">Success</option>
                                        <option value="warning">Warning</option>
                                        <option value="error">Error</option>
                                        <option value="approve">Approve</option>
                                        <option value="reject">Reject</option>
                                        <option value="comment">Comment</option>
                                        <option value="return">Return</option>
                                    </select>
                                    <div className="flex gap-2 ml-auto">
                                        <button 
                                            onClick={() => handleExport(activity, activitySearch, activityType, 'csv')}
                                            className="px-3 py-2 text-xs font-bold bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors flex items-center gap-2"
                                        >
                                            <Download className="w-4 h-4" /> Export CSV
                                        </button>
                                        <button 
                                            onClick={() => handleExport(activity, activitySearch, activityType, 'json')}
                                            className="px-3 py-2 text-xs font-bold bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors flex items-center gap-2"
                                        >
                                            <Download className="w-4 h-4" /> Export JSON
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between text-sm">
                                    <div className="flex gap-3 items-center text-zinc-500">
                                        <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-emerald-500" /> Persistent log</span>
                                        <span className="flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-amber-500" /> Entries remain until deleted in DB</span>
                                    </div>
                                    <span className="text-xs font-semibold text-zinc-500">{activity.length} records</span>
                                </div>

                                <div className="border border-zinc-100 rounded-xl divide-y max-h-[520px] overflow-y-auto custom-scrollbar">
                                    {isActivityLoading ? (
                                        <div className="p-6 text-sm text-zinc-500">Loading...</div>
                                    ) : (
                                        activity
                                            .filter((n) => {
                                                const term = activitySearch.toLowerCase();
                                                const matchesSearch =
                                                    !term ||
                                                    (n.title || '').toLowerCase().includes(term) ||
                                                    (n.message || '').toLowerCase().includes(term) ||
                                                    (n.user || '').toLowerCase().includes(term) ||
                                                    (n.targetView || '').toLowerCase().includes(term) ||
                                                    (n.targetId || '').toLowerCase().includes(term);
                                                const matchesType = activityType === 'all' ? true : (n.type || '').toLowerCase() === activityType;
                                                return matchesSearch && matchesType;
                                            })
                                            .map((n) => {
                                                const formatted = n.time
                                                    ? new Date(n.time).toLocaleString(undefined, {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                    })
                                                    : '';
                                                const icon = n.source === 'workflow' ? <Activity className="w-5 h-5 text-emerald-600" /> : <Bell className="w-5 h-5 text-teal-600" />;
                                                return (
                                                    <div key={n.id} className="p-4 flex items-start gap-3 hover:bg-zinc-50 transition-colors">
                                                        <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center">
                                                            {icon}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex justify-between items-start gap-3">
                                                                <div>
                                                                    <div className="text-sm font-bold text-zinc-900 truncate">{n.title || 'Event'}</div>
                                                                    <div className="text-xs text-zinc-500 leading-snug">{n.message}</div>
                                                                    <div className="text-[10px] text-zinc-400 mt-1 flex gap-2 flex-wrap">
                                                                        <span className="px-2 py-0.5 bg-zinc-100 rounded-full font-bold">Type: {n.type || 'info'}</span>
                                                                        {n.source && <span className="px-2 py-0.5 bg-zinc-100 rounded-full font-bold">Source: {n.source}</span>}
                                                                        {n.user && <span className="px-2 py-0.5 bg-zinc-100 rounded-full font-bold">User: {n.user}</span>}
                                                                        {n.stage && <span className="px-2 py-0.5 bg-zinc-100 rounded-full font-bold">Stage: {n.stage}</span>}
                                                                        {n.action && <span className="px-2 py-0.5 bg-zinc-100 rounded-full font-bold">Action: {n.action}</span>}
                                                                        {n.targetView && <span className="px-2 py-0.5 bg-zinc-100 rounded-full font-bold">View: {n.targetView}</span>}
                                                                        {n.targetId && <span className="px-2 py-0.5 bg-zinc-100 rounded-full font-mono">ID: {n.targetId}</span>}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-1 text-[10px] font-bold text-zinc-400 whitespace-nowrap">
                                                                    <Calendar className="w-3 h-3" /> {formatted}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                    )}
                                    {!isActivityLoading && activity.filter((n) => {
                                        const term = activitySearch.toLowerCase();
                                        return (
                                            !term ||
                                            (n.title || '').toLowerCase().includes(term) ||
                                            (n.message || '').toLowerCase().includes(term)
                                        );
                                    }).length === 0 && (
                                        <div className="p-6 text-sm text-zinc-500 text-center">No activity found.</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};
